import { CommitmentServer } from "@mod/commitment";

export interface User {
  id: string;
  username: string;
  displayName: string;
  isActive: boolean;
  roles: (keyof AppRole)[];
  groups: string[];
  events: string[];
  isAdmin?: boolean;
  serverData?: CommitmentServer;
}

export interface AppRole {
  "edit-groups": boolean;
  "edit-events": boolean;
  auth: boolean;
  "view-guests": boolean;
  "edit-guests": boolean;
  "view-workshops": boolean;
  "edit-workshops": boolean;
  "view-rooms": boolean;
  "edit-rooms": boolean;
  "view-logs": boolean;
  "view-payments": boolean;
  "edit-payments": boolean;
  "remove-guest": boolean;
  "remove-workshop": boolean;
  "transfer-guests": boolean;
  "view-dashboard": boolean;
  "view-events": boolean;
  "view-services": boolean;
  "edit-services": boolean;
  "edit-meetings": boolean;
  "view-invitations": boolean;
  "edit-invitations": boolean;
  "view-comissions": boolean;
  "edit-comissions": boolean;
  "remove-payments": boolean;
  "view-commitments": boolean;
  "edit-commitments": boolean;
  "view-commitment-servers": boolean;
  "edit-commitment-servers": boolean;
  "view-commitment-payments": boolean;
  "edit-commitment-payments": boolean;
  "remove-commitment-payments": boolean;
  "view-commitment-servers-for-event": boolean;
  "view-event-payments": boolean;
  // prayer requests
  "view-prayer-requests": boolean;
  "edit-prayer-requests": boolean;
  // preaching
  "view-preaching": boolean;
  "edit-preaching": boolean;
  "view-preaching-events": boolean;
  "edit-preaching-events": boolean;
  "view-planning-meetings": boolean;
  "edit-planning-meetings": boolean;
  "generate-preaching-assignments": boolean;
  "view-preaching-assignments": boolean;
  "edit-preaching-assignments": boolean;
  "preaching-liaison": boolean;
  // meeting attendance
  "view-meeting-attendance": boolean;
  "edit-meeting-attendance": boolean;
  "edit-meeting-attendance-secondary": boolean;
  "landing-admin": boolean;
  "move-participants": boolean;
}

export const rolesInitialValue: AppRole = {
  "edit-groups": false,
  "edit-events": false,
  auth: false,
  "view-guests": false,
  "edit-guests": false,
  "view-workshops": false,
  "edit-workshops": false,
  "view-rooms": false,
  "edit-rooms": false,
  "view-logs": false,
  "view-payments": false,
  "edit-payments": false,
  "remove-guest": false,
  "remove-workshop": false,
  "transfer-guests": false,
  "view-dashboard": false,
  "view-events": false,
  "view-services": false,
  "edit-services": false,
  "edit-meetings": false,
  "view-invitations": false,
  "edit-invitations": false,
  "view-comissions": false,
  "edit-comissions": false,
  "remove-payments": false,
  "view-commitments": false,
  "edit-commitments": false,
  "view-commitment-servers": false,
  "edit-commitment-servers": false,
  "view-commitment-payments": false,
  "edit-commitment-payments": false,
  "remove-commitment-payments": false,
  "view-commitment-servers-for-event": false,
  "view-event-payments": false,
  // prayer requests
  "view-prayer-requests": false,
  "edit-prayer-requests": false,
  // preaching
  "view-preaching": false,
  "edit-preaching": false,
  "view-preaching-events": false,
  "edit-preaching-events": false,
  "view-planning-meetings": false,
  "edit-planning-meetings": false,
  "generate-preaching-assignments": false,
  "view-preaching-assignments": false,
  "edit-preaching-assignments": false,
  "preaching-liaison": false,
  // meeting attendance
  "view-meeting-attendance": false,
  "edit-meeting-attendance": false,
  "edit-meeting-attendance-secondary": false,
  "landing-admin": false,
  "move-participants": false
};

export const getRoleName = (role: keyof AppRole): string => {
  const roleNames: Record<keyof AppRole, string> = {
    "edit-groups": "Editar Grupos",
    "edit-events": "Editar Eventos",
    auth: "Autenticación",
    "view-guests": "Ver Invitados",
    "edit-guests": "Editar Invitados",
    "view-workshops": "Ver Talleres",
    "edit-workshops": "Editar Talleres",
    "view-rooms": "Ver Cuartos",
    "edit-rooms": "Editar Cuartos",
    "view-logs": "Ver Logs",
    "view-payments": "Ver Pagos",
    "edit-payments": "Editar Pagos",
    "remove-guest": "Eliminar Invitado",
    "remove-workshop": "Eliminar Taller",
    "transfer-guests": "Transferir Invitados",
    "view-dashboard": "Ver Dashboard",
    "view-events": "Ver Eventos",
    "view-services": "Ver Servicios",
    "edit-services": "Editar Servicios",
    "edit-meetings": "Editar Reuniones y Servicios",
    "view-invitations": "Ver Invitaciones",
    "edit-invitations": "Editar Invitaciones",
    "view-comissions": "Ver Comisiones",
    "edit-comissions": "Editar Comisiones",
    "remove-payments": "Eliminar Pagos",
    "view-commitments": "Ver Compromisos",
    "edit-commitments": "Editar Compromisos",
    "view-commitment-servers": "Ver Servidores de Compromisos",
    "edit-commitment-servers": "Editar Servidores de Compromisos",
    "view-commitment-payments": "Ver Pagos de Compromisos",
    "edit-commitment-payments": "Editar Pagos de Compromisos",
    "remove-commitment-payments": "Eliminar Pagos de Compromisos",
    "view-commitment-servers-for-event": "Ver Servidores de Compromisos para Eventos",
    "view-event-payments": "Ver Todos los pagos (Retiros)",
    // prayer requests
    "view-prayer-requests": "Ver Peticiones de Oración",
    "edit-prayer-requests": "Editar Peticiones de Oración",
    // preaching
    "view-preaching": "Ver Predicas",
    "edit-preaching": "Editar Predicas",
    "view-preaching-events": "Ver Eventos de Predica",
    "edit-preaching-events": "Editar Eventos de Predica",
    "view-planning-meetings": "Ver Reuniones de Planificación",
    "edit-planning-meetings": "Editar Reuniones de Planificación",
    "generate-preaching-assignments": "Generar Asignaciones de Predica",
    "view-preaching-assignments": "Ver Asignaciones de Predica",
    "edit-preaching-assignments": "Editar Asignaciones de Predica",
    "preaching-liaison": "Enlace de Predica",
    "view-meeting-attendance": "Ver Asistencia a Reuniones",
    "edit-meeting-attendance": "Editar Asistencia a Reuniones",
    "edit-meeting-attendance-secondary": "Editar Asistencia a Reuniones (Secundario)",
    "landing-admin": "Administrar Landing Page",
    "move-participants": "Mover Participantes"
  };
  return roleNames[role] || role;
};

export const appRoles: (keyof AppRole)[] = Array.from(Object.keys(rolesInitialValue) as (keyof AppRole)[]);

export type RoleModuleId =
  | "groups"
  | "workshopsRooms"
  | "payments"
  | "events"
  | "invitations"
  | "services"
  | "commitments"
  | "prayerRequests"
  | "preaching"
  | "administration"
  | "other";

export interface RoleModule {
  id: RoleModuleId;
  roles: (keyof AppRole)[];
}

/** Roles grouped by the app module they apply to. */
export const roleModules: RoleModule[] = [
  {
    id: "groups",
    roles: [
      "edit-groups",
      "view-dashboard",
      "view-guests",
      "edit-guests",
      "remove-guest",
      "transfer-guests",
      "move-participants",
      "view-comissions",
      "edit-comissions"
    ]
  },
  {
    id: "workshopsRooms",
    roles: ["view-workshops", "edit-workshops", "remove-workshop", "view-rooms", "edit-rooms"]
  },
  { id: "payments", roles: ["view-payments", "edit-payments", "remove-payments", "view-event-payments"] },
  { id: "events", roles: ["view-events", "edit-events"] },
  { id: "invitations", roles: ["view-invitations", "edit-invitations"] },
  {
    id: "services",
    roles: [
      "view-services",
      "edit-services",
      "edit-meetings",
      "view-meeting-attendance",
      "edit-meeting-attendance",
      "edit-meeting-attendance-secondary"
    ]
  },
  {
    id: "commitments",
    roles: [
      "view-commitments",
      "edit-commitments",
      "view-commitment-servers",
      "edit-commitment-servers",
      "view-commitment-servers-for-event",
      "view-commitment-payments",
      "edit-commitment-payments",
      "remove-commitment-payments"
    ]
  },
  { id: "prayerRequests", roles: ["view-prayer-requests", "edit-prayer-requests"] },
  {
    id: "preaching",
    roles: [
      "view-preaching",
      "edit-preaching",
      "view-preaching-events",
      "edit-preaching-events",
      "view-planning-meetings",
      "edit-planning-meetings",
      "view-preaching-assignments",
      "edit-preaching-assignments",
      "generate-preaching-assignments",
      "preaching-liaison"
    ]
  },
  { id: "administration", roles: ["auth", "view-logs", "landing-admin"] }
];

/**
 * Same as `roleModules`, plus an "other" module containing any role that was
 * not assigned to a module (so newly added roles are never hidden).
 */
export const getRoleModules = (): RoleModule[] => {
  const assigned = new Set(roleModules.flatMap((module) => module.roles));
  const unassigned = appRoles.filter((role) => !assigned.has(role));
  return unassigned.length ? [...roleModules, { id: "other", roles: unassigned }] : roleModules;
};

export interface AuthStore {
  user?: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  token?: string;
  refreshToken?: string;
}

export type AuthStoreActions = {
  updateUser: (user: User) => void;
  reset: () => void;
  login: (token: string, refreshToken: string) => void;
  logout: () => void;
};
